import { globeTextureUrls } from './constants';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ThreeNs = any;

const DAY_NIGHT_VERTEX = `
varying vec3 vNormal;
varying vec2 vUv;
void main() {
  vNormal = normalize(normalMatrix * normal);
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const DAY_NIGHT_FRAGMENT = `
#define PI 3.141592653589793
uniform sampler2D dayTexture;
uniform sampler2D nightTexture;
uniform vec2 sunPosition;
uniform vec2 globeRotation;
varying vec3 vNormal;
varying vec2 vUv;

float toRad(in float a) {
  return a * PI / 180.0;
}

vec3 polarToCartesian(vec2 c) {
  float theta = toRad(90.0 - c.x);
  float phi = toRad(90.0 - c.y);
  return vec3(
    sin(phi) * cos(theta),
    cos(phi),
    sin(phi) * sin(theta)
  );
}

void main() {
  float invLon = toRad(globeRotation.x);
  float invLat = -toRad(globeRotation.y);
  mat3 rotX = mat3(
    1.0, 0.0, 0.0,
    0.0, cos(invLat), -sin(invLat),
    0.0, sin(invLat), cos(invLat)
  );
  mat3 rotY = mat3(
    cos(invLon), 0.0, sin(invLon),
    0.0, 1.0, 0.0,
    -sin(invLon), 0.0, cos(invLon)
  );
  vec3 sunDir = rotX * rotY * polarToCartesian(sunPosition);
  float intensity = dot(normalize(vNormal), normalize(sunDir));
  vec4 dayColor = texture2D(dayTexture, vUv);
  vec4 nightColor = texture2D(nightTexture, vUv);
  float blend = smoothstep(-0.12, 0.22, intensity);
  vec3 color = mix(nightColor.rgb, dayColor.rgb, blend);
  float dayBoost = blend * 0.08;
  gl_FragColor = vec4(color + dayBoost, 1.0);
}
`;

/** Subsolar point [lng, lat] for a given instant (UTC-based astronomy). */
export function getSunPosition(date = new Date()): [number, number] {
  const y = date.getUTCFullYear();
  const startOfYear = Date.UTC(y, 0, 0);
  const dayOfYear = (date.getTime() - startOfYear) / 86400000;
  const declination =
    23.44 * Math.sin((2 * Math.PI * (dayOfYear - 81)) / 365.25) * (Math.PI / 180);

  const utcDayStart = Date.UTC(y, date.getUTCMonth(), date.getUTCDate());
  const dayFraction = (date.getTime() - utcDayStart) / 86400000;
  const longitude = dayFraction * 360 - 180;

  return [longitude, (declination * 180) / Math.PI];
}

export function formatIranTime(date = new Date()): string {
  return new Intl.DateTimeFormat('fa-IR', {
    timeZone: 'Asia/Tehran',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

export async function createDayNightGlobeMaterial(
  THREE: ThreeNs,
): Promise<ThreeNs> {
  const loader = new THREE.TextureLoader();
  const [dayTexture, nightTexture] = await Promise.all([
    loader.loadAsync(globeTextureUrls.day),
    loader.loadAsync(globeTextureUrls.night),
  ]);

  dayTexture.anisotropy = 8;
  nightTexture.anisotropy = 8;

  return new THREE.ShaderMaterial({
    uniforms: {
      dayTexture: { value: dayTexture },
      nightTexture: { value: nightTexture },
      sunPosition: { value: new THREE.Vector2() },
      globeRotation: { value: new THREE.Vector2() },
    },
    vertexShader: DAY_NIGHT_VERTEX,
    fragmentShader: DAY_NIGHT_FRAGMENT,
  });
}

export function updateDayNightUniforms(
  material: ThreeNs,
  date: Date,
  globeRotation?: { lng: number; lat: number },
) {
  const [sunLng, sunLat] = getSunPosition(date);
  material.uniforms.sunPosition.value.set(sunLng, sunLat);
  if (globeRotation) {
    material.uniforms.globeRotation.value.set(globeRotation.lng, globeRotation.lat);
  }
}

/** Place main directional light toward the sun for extra depth on the day hemisphere. */
export function sunDirectionVector(sunLng: number, sunLat: number, radius = 4): ThreeNs {
  const phi = ((90 - sunLat) * Math.PI) / 180;
  const theta = ((90 - sunLng) * Math.PI) / 180;
  return {
    x: radius * Math.sin(phi) * Math.cos(theta),
    y: radius * Math.cos(phi),
    z: radius * Math.sin(phi) * Math.sin(theta),
  };
}
